// Google Picker loader (google-sheets-lead-import Phase 1).
//
// Lazy-loads https://apis.google.com/js/api.js once (promise-cached), loads the
// `picker` module, and opens a spreadsheet picker. Config (API key, app id,
// short-lived drive.file access token) comes from GET
// /api/crm-import/gsheets/picker-config at runtime — NOT from REACT_APP_* build
// vars, so setting it on the box never needs a frontend rebuild.
//
// setAppId(app_id) is REQUIRED: it is what attaches the drive.file grant to the
// picked file so the backend can read it afterwards.

declare global {
  interface Window {
    gapi?: any;
    google?: any;
  }
}

const GAPI_SRC = 'https://apis.google.com/js/api.js';

let gapiPromise: Promise<void> | null = null;
let pickerPromise: Promise<void> | null = null;

function loadGapi(): Promise<void> {
  if (window.gapi) return Promise.resolve();
  if (gapiPromise) return gapiPromise;
  gapiPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GAPI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gapiPromise = null;
      script.remove();
      reject(new Error('Could not load Google Picker'));
    };
    document.head.appendChild(script);
  });
  return gapiPromise;
}

function loadPicker(): Promise<void> {
  if (window.google?.picker) return Promise.resolve();
  if (pickerPromise) return pickerPromise;
  pickerPromise = loadGapi().then(
    () =>
      new Promise<void>((resolve, reject) => {
        try {
          window.gapi.load('picker', {
            callback: () => resolve(),
            onerror: () => reject(new Error('Could not load Google Picker')),
          });
        } catch (e) {
          reject(e instanceof Error ? e : new Error('Could not load Google Picker'));
        }
      }),
  );
  pickerPromise.catch(() => { pickerPromise = null; });
  return pickerPromise;
}

export interface PickerOptions {
  apiKey: string;
  appId: string;
  accessToken: string;
}

export interface PickedSpreadsheet {
  id: string;
  name: string;
}

/** Opens Google Picker on the user's spreadsheets. Resolves null on cancel. */
export async function openSpreadsheetPicker({ apiKey, appId, accessToken }: PickerOptions): Promise<PickedSpreadsheet | null> {
  await loadPicker();
  const gp = window.google.picker;
  return new Promise<PickedSpreadsheet | null>((resolve) => {
    const view = new gp.DocsView(gp.ViewId.SPREADSHEETS)
      .setMode(gp.DocsViewMode.LIST)
      .setMimeTypes('application/vnd.google-apps.spreadsheet')
      .setIncludeFolders(false)
      .setSelectFolderEnabled(false);
    const picker = new gp.PickerBuilder()
      .addView(view)
      .setAppId(appId)
      .setOAuthToken(accessToken)
      .setDeveloperKey(apiKey)
      .setTitle('Choose a Google Sheet of leads')
      .setCallback((data: any) => {
        const action = data?.[gp.Response.ACTION];
        if (action === gp.Action.PICKED) {
          const doc = data[gp.Response.DOCUMENTS]?.[0];
          resolve(doc ? { id: String(doc[gp.Document.ID]), name: String(doc[gp.Document.NAME] || '') } : null);
        } else if (action === gp.Action.CANCEL) {
          resolve(null);
        }
      })
      .build();
    picker.setVisible(true);
  });
}
